import { useState } from "react";
import { DndContext, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AlertCircle, Check, Eye, Loader2, PenLine, Plus, X } from "lucide-react";
import { buildPhotoSpreads } from "./photoSpreads";
import { classNames } from "../../helpers/classNames";
import { getGridThumbnailUrl } from "../../util/pendingImageStorage";

/**
 * The Page photos grid (docs/design/HANDOFF.md, "Page photos grid shows facing
 * pages"): photos in the spreads they print in (photoSpreads.js), each a light
 * card of two pages with a "Pages X and Y" label; 2 spreads per row on mobile
 * and tablet, 3 on desktop. The first spread is the dedication page beside
 * photo 1; a half-filled spread ends with a blank page. Pairs are
 * positions: moving a photo shifts the rest. Each tile shows its page number, a
 * "Worth a look" badge when the auto crop was unsure, and a spinner while the
 * photo is being prepared. Tap opens the crop screen; press and hold to move
 * (on touch a quick swipe over the tiles scrolls the page instead).
 * A small X in the corner removes the photo (the page undoes it with the
 * message bar).
 *
 * The "Add photos" tile at the end opens the picker and, on desktop (where it
 * reads "Add"), takes photos dropped on it.
 *
 * Busy (photos still being added or prepared): no tile can be tapped or
 * dragged until they're stored; the X still works.
 *
 * Locked (pages drawing): tiles can't be tapped or dragged; drawn ones get a
 * green check, the rest a light overlay with a spinner. The add tile is
 * hidden.
 *
 * Unsaved (Next couldn't save the photo's crop): a red ring and alert badge in
 * place of "Worth a look", pointed to by the page's red note.
 * TODO(design): not in docs/design; built from Preview's failed page (GAPS.md).
 */
const keyOf = (img) => img.id ?? img._id;

const PhotoTile = ({ image, index, onOpen, onRemove, locked, drawn, preparing, busy, unsaved }) => {
  const id = keyOf(image);
  const failed = unsaved && !locked && !preparing;
  const flagged = !!image.cropReview?.flagged && !locked && !preparing && !failed;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: locked || preparing || busy,
  });
  // "manipulation", not "none": the browser must still scroll on a swipe. Once the
  // long press starts a drag, dnd-kit cancels the touchmoves so the page holds still.
  const style = { transform: CSS.Transform.toString(transform), transition, touchAction: "manipulation", WebkitTouchCallout: "none" };
  const src = getGridThumbnailUrl(image);
  const label = locked
    ? `Page ${index + 1}${drawn ? ", drawn" : ", still drawing"}`
    : failed
      ? `Page ${index + 1} photo, didn't save`
      : flagged
      ? `Page ${index + 1} photo, crop is worth a look`
      : `Page ${index + 1} photo options`;

  const inner = (
    <>
      {src ? <img src={src} alt="" className="pointer-events-none h-full w-full object-cover" /> : null}
      <span className="absolute left-1 top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[rgba(27,13,18,0.78)] px-[5px] text-[11px] font-semibold text-white md:h-6 md:min-w-[24px] md:text-[13px] lg:h-[22px] lg:min-w-[22px] lg:text-xs">
        {index + 1}
      </span>
      {flagged ? (
        <>
          {/* Small tiles (phones, and desktop below xl) get the eye badge; the label needs the room. */}
          <span aria-hidden="true" className="absolute bottom-[5px] left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-amber-500 bg-amber-300 text-amber-950 shadow-[0_2px_6px_rgba(27,13,18,0.28)] md:hidden lg:flex xl:hidden">
            <Eye className="h-3.5 w-3.5" />
          </span>
          <span className="absolute bottom-1.5 left-1/2 hidden h-[22px] -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full border border-amber-500 bg-amber-300 px-[7px] text-[11px] font-bold text-amber-950 shadow-[0_2px_6px_rgba(27,13,18,0.28)] md:flex lg:hidden xl:flex">
            <Eye className="h-3 w-3" aria-hidden="true" />
            Worth a look
          </span>
        </>
      ) : null}
      {failed ? (
        <span aria-hidden="true" className="absolute bottom-[5px] left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-red-700 text-white shadow-[0_2px_6px_rgba(27,13,18,0.28)]">
          <AlertCircle className="h-4 w-4" strokeWidth={2.4} />
        </span>
      ) : null}
      {preparing || (locked && !drawn) ? (
        <span className="absolute inset-0 flex items-center justify-center bg-[rgba(251,248,243,0.62)] text-md-pink-dark">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        </span>
      ) : null}
      {locked && drawn ? (
        <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-green-800 text-white">
          <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
        </span>
      ) : null}
    </>
  );

  const classes = classNames(
    "relative block aspect-[3/4] w-full overflow-hidden rounded-md bg-md-pink-light-1 text-left",
    failed ? "ring-[3px] ring-red-700" : flagged ? "ring-2 ring-amber-500" : ""
  );

  if (locked) {
    return (
      <div ref={setNodeRef} style={style} role="img" aria-label={label} className={classes}>
        {inner}
      </div>
    );
  }
  // The sortable wrapper moves as one piece; the tile and the remove control are siblings inside it.
  return (
    <div ref={setNodeRef} style={style} className={classNames("relative", isDragging && "z-10 opacity-90")}>
      <button
        type="button"
        aria-label={label}
        disabled={preparing || busy}
        onClick={() => onOpen(image)}
        className={classNames(classes, "select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink", (preparing || busy) && "cursor-wait", isDragging && "shadow-xl")}
        {...attributes}
        {...listeners}
      >
        {inner}
      </button>
      {onRemove && !preparing ? (
        <button
          type="button"
          aria-label={`Remove page ${index + 1} photo`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(image);
          }}
          className="absolute -right-1.5 -top-1.5 flex h-9 w-9 items-center justify-center rounded-full text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
        >
          <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[rgba(27,13,18,0.78)] shadow-[0_1px_4px_rgba(27,13,18,0.4)] md:h-6 md:w-6">
            <X className="h-3 w-3 md:h-3.5 md:w-3.5" strokeWidth={2.5} aria-hidden="true" />
          </span>
        </button>
      ) : null}
    </div>
  );
};

export const AddPhotosTile = ({ onClick, onDropFiles = null }) => {
  const [dragOver, setDragOver] = useState(false);
  const dropProps = onDropFiles
    ? {
        onDragOver: (e) => {
          e.preventDefault();
          setDragOver(true);
        },
        onDragLeave: () => setDragOver(false),
        onDrop: (e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer?.files?.length) onDropFiles(e.dataTransfer.files);
        },
      }
    : {};
  return (
    <button
      type="button"
      aria-label="Add photos"
      onClick={onClick}
      {...dropProps}
      className={classNames(
        "flex aspect-[3/4] w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed bg-white p-1 text-center text-xs font-semibold leading-tight text-md-pink-dark hover:border-md-pink-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink md:text-[13px]",
        dragOver ? "border-md-pink-dark bg-md-pink/10" : "border-[#C9BCB0]"
      )}
    >
      <Plus className="h-5 w-5" aria-hidden="true" />
      <span aria-hidden="true">Add photos</span>
    </button>
  );
};

/** The inside front cover, where the dedication prints (shown whether or not one is written yet). */
const DedicationTile = () => (
  <div
    role="img"
    aria-label="Dedication page"
    className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-md-control bg-white p-1 text-center text-md-pink-dark"
  >
    <PenLine className="h-[18px] w-[18px]" aria-hidden="true" />
    <span className="text-[11px] font-semibold leading-tight text-md-muted md:text-xs">Dedication page</span>
  </div>
);

/** A page that prints blank (no dedication; a half-filled spread), or the inside back cover (facing photo 20). */
const BlankTile = ({ label = "Blank" }) => (
  <div role="img" aria-label={label === "Blank" ? "Blank page" : label} className="flex aspect-[3/4] w-full items-center justify-center rounded-md bg-[#F6F1EA] p-1 text-center text-[11px] leading-tight text-[#8A8284] md:text-xs">
    {label}
  </div>
);

const PhotoGrid = ({
  images,
  onReorder,
  onOpen,
  onRemove = null,
  onAdd,
  onDropFiles = null,
  locked = false,
  isDrawn = () => false,
  isPreparing = () => false,
  isUnsaved = () => false,
  busy = false,
  showAdd = true,
}) => {
  // Mouse drags start after a small move. Touch drags need a press and hold, and a
  // finger that moves before then is scrolling. (PointerSensor would also catch
  // touches and turn every swipe into a drag.)
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 8 } })
  );

  const handleDragEnd = ({ active, over }) => {
    if (locked || !over || active.id === over.id) return;
    const from = images.findIndex((img) => keyOf(img) === active.id);
    const to = images.findIndex((img) => keyOf(img) === over.id);
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(images, from, to));
  };

  const spreads = buildPhotoSpreads({ count: images.length, showAdd: showAdd && !locked });

  const slot = (item) => {
    if (item.kind === "photo") {
      const image = images[item.index];
      return (
        <PhotoTile
          key={keyOf(image)}
          image={image}
          index={item.index}
          onOpen={onOpen}
          onRemove={onRemove}
          locked={locked}
          drawn={isDrawn(image)}
          preparing={isPreparing(image)}
          unsaved={isUnsaved(image)}
          busy={busy}
        />
      );
    }
    if (item.kind === "dedication") return <DedicationTile key="dedication" />;
    if (item.kind === "add") return <AddPhotosTile key="add" onClick={onAdd} onDropFiles={onDropFiles} />;
    if (item.kind === "insideBack") return <BlankTile key="inside-back" label="Inside back cover" />;
    return <BlankTile key={`blank-${item.kind}`} />;
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={images.map(keyOf)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 gap-x-3 gap-y-3.5 md:gap-x-4 md:gap-y-[18px] lg:grid-cols-3">
          {spreads.map((spread) => (
            <div key={spread.label} className="flex flex-col gap-1.5">
              <div className="relative grid grid-cols-2 gap-[3px] rounded-[10px] bg-[#EBE2D7] p-1">
                <span aria-hidden="true" className="pointer-events-none absolute bottom-1.5 left-1/2 top-1.5 w-px bg-[rgba(27,13,18,0.12)]" />
                {spread.slots.map(slot)}
              </div>
              <span className="text-center text-xs text-md-muted">{spread.label}</span>
            </div>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default PhotoGrid;
